import { LiaDataElement } from '@liasoft/lit-ui';
import { html, nothing } from 'lit';
import { navigate } from '../../router.js';
import { SeatApiError, accountOverview } from '../api/developer.js';
import { currentParams, onUrlChange } from '../../shared/state/urlState.js';
import { errorPanel, loadingPanel } from '../../shared/ui/templates.js';
import { breadcrumb } from '../../shared/ui/breadcrumb.js';
import { ACCOUNTS_PATH, ACCOUNT_PARAM, SECTIONS, accountPath, isPersonal } from '../accounts.js';
import { getAccounts, loadAccounts } from '../state/context.js';

/**
 * The frame every page about one team shares.
 *
 * Six pages - overview, members, repositories, tokens, billing, settings - and one subject, named in
 * the address as `?account=<id>` and, for the reader, by the sidebar the shell draws. Each page loads
 * the same thing, `GET /api/v1/seats/accounts/{id}`, which is the whole of what an owner is shown about
 * a team in one request; a page then renders its own slice of it. One request rather than one per
 * panel, because the answer is small and every page needs the heading, the usage figure and the owner
 * check anyway.
 *
 * <b>Ownership is decided by the server and only shown here.</b> The endpoint answers 404 for a team
 * the caller does not own, and this frame turns that into a sentence rather than an error - but
 * nothing on these pages is a control; hiding a button is a convenience, and the API refuses
 * regardless.
 *
 * A personal team has three of the six pages. A link to one of the others is answered by the router
 * once the list of teams is loaded, and here, from the team itself, when it is not yet - a cold link.
 */
export class AccountView extends LiaDataElement
{
   /** Which section this page belongs to. Set by the subclass; the sidebar marks it. */
   static section = '';

   /**
    * For a page underneath a section - the invitations under members, say - what it is called and where
    * it lives. The breadcrumb then reads Accounts, the account, the section, this page; the section's
    * own page has no entry here.
    */
   static subpage = null;

   static properties = {
      /** The account id. Read from the address, and settable so a harness can hand one in. */
      account: { type: String },

      team: { state: true },
      missing: { state: true },
      notice: { state: true },
      secret: { state: true },
      busy: { state: true }
   };

   constructor()
   {
      super();
      this.account = currentParams().get(ACCOUNT_PARAM) ?? '';
      this.team = null;
      this.missing = false;
      this.notice = null;

      // A token or an invitation link, shown once and never again. Held in state rather than in the
      // account, because it does not survive a reload and must not look as though it does.
      this.secret = null;
      this.busy = false;
   }

   connectedCallback()
   {
      super.connectedCallback();

      // Following a link to another account on the same page is a query-string change, and the element
      // stays where it is - so it re-reads its subject rather than keeping the old one under the new
      // address.
      this._unurl = onUrlChange(() =>
      {
         const next = currentParams().get(ACCOUNT_PARAM) ?? '';

         if (next !== this.account)
         {
            this.account = next;
            void this.reload();
         }
      });
   }

   disconnectedCallback()
   {
      this._unurl?.();
      super.disconnectedCallback();
   }

   async load(signal)
   {
      this.missing = false;

      if (!this.account)
      {
         this.team = null;
         return;
      }

      try
      {
         this.team = await accountOverview(this.account, signal);

         const section = SECTIONS.find(entry => entry.id === this.constructor.section);

         if (isPersonal(this.team) && section && !section.personal)
         {
            navigate(accountPath('overview', this.account), { replace: true });
         }
      }
      catch (error)
      {
         // Not yours and not there read the same from the API, on purpose, and here they read as one
         // explanation rather than as a failure of the page.
         if (error instanceof SeatApiError && error.status === 404)
         {
            this.team = null;
            this.missing = true;
            return;
         }

         throw error;
      }
   }

   /**
    * Runs one action, tells the reader what happened, and re-reads the account.
    *
    * `describe` turns the result into a notice - a sentence, or `{ notice, secret }` when the action
    * handed back something readable exactly once. Answers whether the action went through, for a form
    * that stays open on a refusal.
    *
    * The sidebar is re-read too: stepping down as owner, or giving up a seat, changes what this person
    * is to the team, and the chrome has to say so without waiting for the next navigation.
    */
   async act(run, describe)
   {
      this.busy = true;
      this.notice = null;
      this.secret = null;

      try
      {
         const result = await run();
         const described = typeof describe === 'function' ? describe(result) : describe;

         if (typeof described === 'string')
         {
            this.notice = { variant: 'success', message: described };
         }
         else if (described)
         {
            this.notice = described.notice ?? null;
            this.secret = described.secret ?? null;
         }

         await Promise.all([this.reload(), loadAccounts()]);

         return true;
      }
      catch (error)
      {
         this.notice = { variant: 'danger', message: error.message };

         return false;
      }
      finally
      {
         this.busy = false;
      }
   }

   /**
    * The account's name, for the trail and the heading.
    *
    * From the owner's view when there is one, and otherwise from the list the shell holds - a member
    * gets no owner's view, and their page still has to say whose it is.
    */
   get name()
   {
      return this.team?.name ?? getAccounts().find(entry => entry.id === this.account)?.name ?? null;
   }

   /**
    * The line under the heading. The owner's view supplies it; a page with another source overrides.
    * A personal team has no plan and no seats to count, so it says only what it is.
    */
   get description()
   {
      if (!this.team)
      {
         return '';
      }

      return `${this.team.name} · ${isPersonal(this.team) ? 'Personal' : planWord(this.team)}`;
   }

   /** The page's own toolbar. Subclasses add to it. */
   get pageActions()
   {
      return [];
   }

   onPageAction(id)
   {
      void id;
   }

   /** What the page shows once the account is loaded. Subclasses supply it. */
   get body()
   {
      return nothing;
   }

   /**
    * Where this page is, from the top: every account, this one, its section, and the page under it.
    *
    * On every page, so nobody has to work out where they are from the address bar. Each entry but the
    * last is a link; the account's own entry leads to its overview.
    */
   get breadcrumb()
   {
      const section = SECTIONS.find(entry => entry.id === this.constructor.section);
      const subpage = this.constructor.subpage;
      const trail = [{ label: 'Teams', url: ACCOUNTS_PATH }];

      if (this.name)
      {
         trail.push({ label: this.name, url: accountPath('overview', this.account) });
      }

      if (section)
      {
         trail.push(subpage
            ? { label: section.label, url: accountPath(section.id, this.account) }
            : { label: section.label });
      }

      if (subpage)
      {
         trail.push({ label: subpage.label });
      }

      return trail;
   }

   render()
   {
      const team = this.team;
      const section = SECTIONS.find(entry => entry.id === this.constructor.section);
      const subpage = this.constructor.subpage;

      return html`
         <lia-page
            title=${subpage?.label ?? section?.label ?? 'Team'}
            icon=${section?.icon ?? 'fa-solid fa-people-group'}
            description=${this.description}
            .actions=${team ? this.pageActions : []}
            @lia-action=${event => this.onPageAction(event.detail?.action?.id)}
         >
            <div class="d-flex flex-column gap-3">
               ${breadcrumb(this.breadcrumb)}
               ${this.error ? errorPanel(this.error, () => this.reload()) : nothing}

               ${this.notice
                  ? html`<lia-alert
                       variant=${this.notice.variant}
                       message=${this.notice.message}
                       dismissible
                       @lia-dismiss=${() => { this.notice = null; }}
                    ></lia-alert>`
                  : nothing}

               ${this.secret ? this.secretCard : nothing}

               ${this.content}
            </div>
         </lia-page>`;
   }

   get content()
   {
      if (!this.account)
      {
         return html`
            <lia-empty-state
               centered
               icon="fa-solid fa-people-group"
               title="No team chosen"
               message="Pick one of your teams to manage it."
               .actions=${[{ id: 'accounts', label: 'Pick a team', href: ACCOUNTS_PATH, variant: 'primary' }]}
            ></lia-empty-state>`;
      }

      if (this.missing)
      {
         return html`
            <lia-empty-state
               centered
               icon="fa-solid fa-lock"
               title="Not one of your teams"
               message="This team is not yours to manage; one of its owners can make you an owner."
               .actions=${[{ id: 'accounts', label: 'Pick another team', href: ACCOUNTS_PATH, variant: 'primary' }]}
            ></lia-empty-state>`;
      }

      if (!this.team)
      {
         return this.error ? nothing : loadingPanel('Loading the team');
      }

      return this.body;
   }

   get secretCard()
   {
      return html`
         <lia-card
            title=${this.secret.label}
            icon="fa-solid fa-eye"
            .content=${html`
               <p class="small text-body-secondary mb-2">${this.secret.note}</p>
               <div class="d-flex align-items-center gap-2">
                  <code class="text-break flex-grow-1">${this.secret.value}</code>
                  <lia-copy-button .text=${this.secret.value}></lia-copy-button>
               </div>`}
         ></lia-card>`;
   }
}

/** "Team plan · 4 of 6 seats" - the one line under the heading that says where the team stands. */
export function planWord(team)
{
   const usage = team.usage ?? {};
   const allowed = (usage.seatQuota ?? 0) + (usage.includedFreeSeats ?? 0);
   const plan = team.plan ? `${team.plan[0].toUpperCase()}${team.plan.slice(1)} plan` : 'No plan';

   return `${plan} · ${usage.countingSeats ?? 0} of ${allowed} seats`;
}
